import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../components/layout/MainLayout';
import { messageService, ConversationSummary, MessageWithSender, GroupWithMembers } from '../api/services/messageService';
import { userService, ActiveUser } from '../api/services/userService';
import { useAuthStore } from '../store/authStore';
import { useMessageStore } from '../store/messageStore';
import { StatusIndicator } from '../components/common/StatusIndicator';
import { toast } from 'react-hot-toast';

interface SelectedConversation {
  type: 'direct' | 'group';
  id: string | number; // UserId for direct, GroupId for group
}

interface ImagePreview {
  url: string;
  name: string;
}

const Messages: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { fetchUnreadCount } = useMessageStore();
  const [selected, setSelected] = useState<SelectedConversation | null>(null);
  const [messageContent, setMessageContent] = useState('');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<ImagePreview | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch conversations (includes both direct and group)
  const { data: conversations = [], isLoading: conversationsLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: messageService.getConversations,
    refetchInterval: 10000,
  });

  // Fetch messages for selected direct conversation
  const { data: directMessages = [], isLoading: directMessagesLoading } = useQuery({
    queryKey: ['conversation', selected?.id],
    queryFn: () => messageService.getConversation(selected!.id as string),
    enabled: selected?.type === 'direct',
    refetchInterval: 5000,
  });

  // Fetch messages for selected group conversation
  const { data: groupMessages = [], isLoading: groupMessagesLoading } = useQuery({
    queryKey: ['groupMessages', selected?.id],
    queryFn: () => messageService.getGroupMessages(selected!.id as number),
    enabled: selected?.type === 'group',
    refetchInterval: 5000,
  });

  // Fetch group details
  const { data: groupDetails } = useQuery({
    queryKey: ['group', selected?.id],
    queryFn: () => messageService.getGroup(selected!.id as number),
    enabled: selected?.type === 'group',
  });

  // Fetch active users - always fetch to have status info available
  const { data: activeUsers = [] } = useQuery({
    queryKey: ['activeUsers'],
    queryFn: userService.getActiveUsers,
    refetchInterval: 30000, // Refresh status every 30 seconds
  });

  // Helper to get user status from activeUsers
  const getUserStatus = (userId: string | null) => {
    if (!userId) return 'offline' as const;
    const activeUser = activeUsers.find((u) => u.UserId === userId);
    return activeUser?.Status || ('offline' as const);
  };

  // Send direct message mutation
  const sendDirectMessageMutation = useMutation({
    mutationFn: (data: { receiverId: string; content: string; attachments?: File[] }) =>
      messageService.sendMessage(data),
    onSuccess: () => {
      setMessageContent('');
      setSelectedFiles([]);
      queryClient.invalidateQueries({ queryKey: ['conversation', selected?.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      toast.error('Failed to send message');
    },
  });

  // Send group message mutation
  const sendGroupMessageMutation = useMutation({
    mutationFn: (data: { groupId: number; content: string; attachments?: File[] }) =>
      messageService.sendGroupMessage(data.groupId, data.content, data.attachments),
    onSuccess: () => {
      setMessageContent('');
      setSelectedFiles([]);
      queryClient.invalidateQueries({ queryKey: ['groupMessages', selected?.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      toast.error('Failed to send message');
    },
  });

  // Create group mutation
  const createGroupMutation = useMutation({
    mutationFn: (data: { name: string; memberIds: string[] }) =>
      messageService.createGroup(data),
    onSuccess: (group) => {
      setShowNewGroupModal(false);
      setGroupName('');
      setSelectedMembers([]);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      setSelected({ type: 'group', id: group.Id });
      toast.success('Group created successfully');
    },
    onError: () => {
      toast.error('Failed to create group');
    },
  });

  // Mark direct messages as read mutation
  const markDirectAsReadMutation = useMutation({
    mutationFn: (otherUserId: string) => messageService.markAsRead(otherUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      fetchUnreadCount();
    },
  });

  // Mark group messages as read mutation
  const markGroupAsReadMutation = useMutation({
    mutationFn: (groupId: number) => messageService.markGroupAsRead(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      fetchUnreadCount();
    },
  });

  // Delete message mutation
  const deleteMessageMutation = useMutation({
    mutationFn: (messageId: number) => messageService.deleteMessage(messageId),
    onSuccess: () => {
      if (selected?.type === 'direct') {
        queryClient.invalidateQueries({ queryKey: ['conversation', selected.id] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['groupMessages', selected?.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Message deleted');
    },
    onError: () => {
      toast.error('Failed to delete message');
    },
  });

  // Delete conversation mutation
  const deleteConversationMutation = useMutation({
    mutationFn: (otherUserId: string) => messageService.deleteConversation(otherUserId),
    onSuccess: () => {
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      fetchUnreadCount();
      toast.success('Conversation deleted');
    },
    onError: () => {
      toast.error('Failed to delete conversation');
    },
  });

  const handleDeleteMessage = (messageId: number) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessageMutation.mutate(messageId);
    }
  };

  const handleDeleteConversation = () => {
    if (selected?.type === 'direct') {
      if (window.confirm('Are you sure you want to delete this entire conversation? This action cannot be undone.')) {
        deleteConversationMutation.mutate(selected.id as string);
      }
    }
  };

  const messages = selected?.type === 'direct' ? directMessages : groupMessages;
  const messagesLoading = selected?.type === 'direct' ? directMessagesLoading : groupMessagesLoading;
  const isSending = sendDirectMessageMutation.isPending || sendGroupMessageMutation.isPending;

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark messages as read when conversation is opened
  useEffect(() => {
    if (selected) {
      const conversation = conversations.find((c) =>
        selected.type === 'direct'
          ? c.OtherUserId === selected.id && !c.IsGroup
          : c.GroupId === selected.id && c.IsGroup
      );
      if (conversation && conversation.UnreadCount > 0) {
        if (selected.type === 'direct') {
          markDirectAsReadMutation.mutate(selected.id as string);
        } else {
          markGroupAsReadMutation.mutate(selected.id as number);
        }
      }
    }
  }, [selected, conversations]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const hasContent = messageContent.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;

    if (!selected || (!hasContent && !hasFiles)) return;

    if (selected.type === 'direct') {
      sendDirectMessageMutation.mutate({
        receiverId: selected.id as string,
        content: messageContent.trim(),
        attachments: selectedFiles.length > 0 ? selectedFiles : undefined,
      });
    } else {
      sendGroupMessageMutation.mutate({
        groupId: selected.id as number,
        content: messageContent.trim(),
        attachments: selectedFiles.length > 0 ? selectedFiles : undefined,
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + selectedFiles.length > 5) {
      toast.error('Maximum 5 files allowed per message');
      return;
    }
    setSelectedFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
  };

  const handleSelectConversation = (conversation: ConversationSummary) => {
    if (conversation.IsGroup && conversation.GroupId) {
      setSelected({ type: 'group', id: conversation.GroupId });
    } else if (conversation.OtherUserId) {
      setSelected({ type: 'direct', id: conversation.OtherUserId });
    }
    setShowNewMessageModal(false);
    setShowNewGroupModal(false);
  };

  const handleSelectUser = (userId: string) => {
    setSelected({ type: 'direct', id: userId });
    setShowNewMessageModal(false);
    setUserSearchQuery('');
  };

  const handleCreateGroup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim() || selectedMembers.length === 0) return;
    createGroupMutation.mutate({
      name: groupName.trim(),
      memberIds: selectedMembers,
    });
  };

  const toggleMemberSelection = (userId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const selectedConversation = conversations.find((c) =>
    selected?.type === 'direct'
      ? c.OtherUserId === selected.id && !c.IsGroup
      : c.GroupId === selected?.id && c.IsGroup
  );

  const getDisplayName = (u: ActiveUser): string => {
    const fullName = [u.FirstName, u.LastName].filter(Boolean).join(' ').trim();
    return fullName || u.UserName;
  };

  const filteredUsers = activeUsers.filter(
    (u) =>
      u.UserId !== user?.UserId &&
      (getDisplayName(u).toLowerCase().includes(userSearchQuery.toLowerCase()) ||
       u.UserName.toLowerCase().includes(userSearchQuery.toLowerCase()))
  );

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getConversationKey = (c: ConversationSummary) =>
    c.IsGroup ? `group-${c.GroupId}` : `user-${c.OtherUserId}`;

  const isSelected = (c: ConversationSummary) =>
    selected &&
    ((selected.type === 'group' && c.IsGroup && c.GroupId === selected.id) ||
      (selected.type === 'direct' && !c.IsGroup && c.OtherUserId === selected.id));

  const getHeaderTitle = () => {
    if (selected?.type === 'group' && groupDetails) {
      return groupDetails.Name;
    }
    if (selectedConversation) {
      return selectedConversation.OtherUserName;
    }
    if (selected?.type === 'direct') {
      const foundUser = activeUsers.find((u) => u.UserId === selected.id);
      return foundUser ? getDisplayName(foundUser) : 'Loading...';
    }
    return 'Loading...';
  };

  return (
    <MainLayout>
      <div className="px-4 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Messages</h1>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowNewGroupModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              New Group
            </button>
            <button
              onClick={() => setShowNewMessageModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Message
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Conversations Sidebar */}
            <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 flex flex-col">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Conversations</h2>
              </div>

              <div className="flex-1 overflow-y-auto">
                {conversationsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No conversations yet. Start a new message!
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <button
                      key={getConversationKey(conversation)}
                      onClick={() => handleSelectConversation(conversation)}
                      className={`w-full p-4 text-left border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        isSelected(conversation) ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            {/* Group icon or Status indicator */}
                            {conversation.IsGroup ? (
                              <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            ) : (
                              <span className="mr-2">
                                <StatusIndicator status={getUserStatus(conversation.OtherUserId)} size="sm" />
                              </span>
                            )}
                            <span className={`font-medium truncate ${
                              conversation.UnreadCount > 0
                                ? 'text-gray-900 dark:text-white'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {conversation.OtherUserName}
                            </span>
                            {conversation.UnreadCount > 0 && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                                {conversation.UnreadCount}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm truncate mt-1 ${
                            conversation.UnreadCount > 0
                              ? 'text-gray-900 dark:text-gray-100 font-medium'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {conversation.IsLastMessageFromMe && (
                              <span className="text-gray-400 dark:text-gray-500">You: </span>
                            )}
                            {conversation.LastMessage}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 whitespace-nowrap">
                          {formatTime(conversation.LastMessageAt)}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Message Thread */}
            <div className="flex-1 flex flex-col">
              {selected ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {selected.type === 'group' ? (
                          <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        ) : (
                          <span className="mr-2">
                            <StatusIndicator status={getUserStatus(selected.id as string)} size="md" />
                          </span>
                        )}
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {getHeaderTitle()}
                          </h2>
                          {selected.type === 'group' && groupDetails && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {groupDetails.members.map((m) => m.UserName).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Delete conversation button (only for direct messages) */}
                      {selected.type === 'direct' && (
                        <button
                          onClick={handleDeleteConversation}
                          disabled={deleteConversationMutation.isPending}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete conversation"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        No messages yet. Start the conversation!
                      </div>
                    ) : (
                      messages.map((message) => {
                        const isOwn = message.SenderId === user?.UserId;
                        return (
                          <div
                            key={message.Id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
                          >
                            {/* Delete button for own messages - shows on left side */}
                            {isOwn && (
                              <button
                                onClick={() => handleDeleteMessage(message.Id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity self-center mr-2 p-1 text-gray-400 hover:text-red-500"
                                title="Delete message"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2 ${
                                isOwn
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                              }`}
                            >
                              {/* Show sender name in group chats */}
                              {selected.type === 'group' && !isOwn && (
                                <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 mb-1">
                                  {message.SenderName}
                                </p>
                              )}
                              {message.Content && (
                                <p className="break-words whitespace-pre-wrap">{message.Content}</p>
                              )}
                              {/* Display attachments */}
                              {message.Attachments && message.Attachments.length > 0 && (
                                <div className={`${message.Content ? 'mt-2' : ''} space-y-2`}>
                                  {message.Attachments.map((attachment) => (
                                    <div key={attachment.Id}>
                                      {isImageFile(attachment.MimeType) ? (
                                        // Image attachment - show inline preview
                                        <div className="relative group/attachment">
                                          <img
                                            src={messageService.getAttachmentViewUrl(attachment.Id)}
                                            alt={attachment.OriginalName}
                                            className="max-w-full max-h-64 rounded cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => setImagePreview({
                                              url: messageService.getAttachmentViewUrl(attachment.Id),
                                              name: attachment.OriginalName,
                                            })}
                                          />
                                          <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-xs truncate ${isOwn ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
                                              {attachment.OriginalName} ({formatFileSize(attachment.FileSize)})
                                            </span>
                                            <a
                                              href={messageService.getAttachmentUrl(attachment.Id)}
                                              download
                                              className={`opacity-0 group-hover/attachment:opacity-100 transition-opacity ${isOwn ? 'text-indigo-200 hover:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                              title="Download"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                              </svg>
                                            </a>
                                          </div>
                                        </div>
                                      ) : (
                                        // Non-image attachment - show as downloadable link
                                        <a
                                          href={messageService.getAttachmentUrl(attachment.Id)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`flex items-center gap-2 p-2 rounded ${
                                            isOwn
                                              ? 'bg-indigo-500 hover:bg-indigo-400'
                                              : 'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500'
                                          }`}
                                        >
                                          <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                          </svg>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm truncate">{attachment.OriginalName}</p>
                                            <p className={`text-xs ${isOwn ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
                                              {formatFileSize(attachment.FileSize)}
                                            </p>
                                          </div>
                                          <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className={`flex items-center gap-1 mt-1 ${
                                isOwn ? 'justify-end' : 'justify-start'
                              }`}>
                                <span className={`text-xs ${
                                  isOwn ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                  {formatTime(message.CreatedAt)}
                                </span>
                                {/* Read indicator for sent messages (direct messages only) */}
                                {isOwn && selected.type === 'direct' && (
                                  <span title={message.IsRead ? `Seen${message.ReadAt ? ` at ${formatTime(message.ReadAt)}` : ''}` : 'Delivered'}>
                                    {message.IsRead ? (
                                      <svg className="h-4 w-4 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    ) : (
                                      <svg className="h-4 w-4 text-indigo-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700">
                    {/* File Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2"
                          >
                            {file.type.startsWith('image/') ? (
                              <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            )}
                            <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[150px] truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex space-x-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                        title="Attach file"
                      >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      </button>
                      <input
                        type="text"
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <button
                        type="submit"
                        disabled={(!messageContent.trim() && selectedFiles.length === 0) || isSending}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSending ? (
                          <span className="flex items-center">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          </span>
                        ) : (
                          'Send'
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="mt-2">Select a conversation or start a new message</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* New Message Modal */}
        {showNewMessageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">New Message</h3>
                <button
                  onClick={() => {
                    setShowNewMessageModal(false);
                    setUserSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4">
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="Search users..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                />

                <div className="mt-4 max-h-64 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                      {userSearchQuery ? 'No users found' : 'Loading users...'}
                    </p>
                  ) : (
                    filteredUsers.map((u) => (
                      <button
                        key={u.UserId}
                        onClick={() => handleSelectUser(u.UserId)}
                        className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <StatusIndicator status={u.Status} size="sm" />
                        <span className="text-gray-900 dark:text-white font-medium">{getDisplayName(u)}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Group Modal */}
        {showNewGroupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Group</h3>
                <button
                  onClick={() => {
                    setShowNewGroupModal(false);
                    setGroupName('');
                    setSelectedMembers([]);
                    setUserSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateGroup} className="p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Enter group name..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Members ({selectedMembers.length} selected)
                  </label>
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    placeholder="Search users..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />

                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                    {filteredUsers.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                        {userSearchQuery ? 'No users found' : 'Loading users...'}
                      </p>
                    ) : (
                      filteredUsers.map((u) => (
                        <label
                          key={u.UserId}
                          className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMembers.includes(u.UserId)}
                            onChange={() => toggleMemberSelection(u.UserId)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-3 mr-2">
                            <StatusIndicator status={u.Status} size="sm" />
                          </span>
                          <span className="text-gray-900 dark:text-white">{getDisplayName(u)}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewGroupModal(false);
                      setGroupName('');
                      setSelectedMembers([]);
                      setUserSearchQuery('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!groupName.trim() || selectedMembers.length === 0 || createGroupMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createGroupMutation.isPending ? 'Creating...' : 'Create Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {imagePreview && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={() => setImagePreview(null)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]">
              {/* Close button */}
              <button
                onClick={() => setImagePreview(null)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {/* Image */}
              <img
                src={imagePreview.url}
                alt={imagePreview.name}
                className="max-w-full max-h-[85vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              {/* Filename and download button */}
              <div className="absolute -bottom-10 left-0 right-0 flex items-center justify-center gap-4">
                <span className="text-white text-sm">{imagePreview.name}</span>
                <a
                  href={imagePreview.url.replace('?view=true', '')}
                  download
                  className="text-white hover:text-gray-300 transition-colors"
                  title="Download"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Messages;
